import { environment } from "@env/environment";
import { type IAuthLoginResponse } from "@interfaces/auth/IAuthLoginResponse";
import { type IResetPasswordResponse } from "@interfaces/auth/IResetPasswordResponse";
import { type IValidateCodeResponse } from "@interfaces/auth/IValidateCodeResponse";
import { Store } from "@ngxs/store";
import {
  ClearAccessibleModules,
  ClearCompanyGeoreference,
  ClearFavorites,
  SetCurrentTenant,
  SetCurrentTenantBusinessName,
} from "@state/common/common.action";
import { ClearSession, SetSession } from "@state/session/session.actions";
import { SessionState } from "@state/session/session.state";
import { SetUser } from "@state/user/user.actions";
import { RutUtils } from "@utils/rut.utils";
import { TokenUtils } from "@utils/token.utils";
import { firstValueFrom } from "rxjs";

import { HttpClient } from "@angular/common/http";
import { inject, type OnDestroy, Service } from "@angular/core";
import { Router } from "@angular/router";

const DEFAULT_SESSION_SECONDS = 4 * 60 * 60;
const REFRESH_LEEWAY_MS = 5 * 60 * 1000;

@Service()
export class AuthService implements OnDestroy {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private refreshPromise: Promise<boolean> | null = null;
  private refreshTimeout: number | null = null;
  private sessionVersion = 0;

  login(username: string, password: string) {
    const rut = RutUtils.format(username)
      .replaceAll(".", "")
      .toLocaleLowerCase();

    return this.httpClient.post<IAuthLoginResponse>(
      `${environment.SSO_URL}/sso/auth/login`,
      {
        username: rut,
        password,
      }
    );
  }

  getAdminAuthorizationUrl() {
    const callbackUri = `${window.location.origin}/auth/admin/callback`;
    const query = new URLSearchParams({ callbackUri });

    return `${environment.SSO_URL}/sso/auth/admin/authorize?${query}`;
  }

  redeemAdminHandoff(handoff: string) {
    return this.httpClient.post<IAuthLoginResponse>(
      `${environment.SSO_URL}/sso/auth/admin/handoff`,
      { handoff }
    );
  }

  logout() {
    const body = {
      refreshToken: this.store.selectSnapshot(SessionState.refreshToken),
    };
    this.stopAutoRefresh();
    const token = this.store.selectSnapshot(SessionState.ssoToken);
    const endpoint =
      token && new TokenUtils(token).isPlatformAdministrator()
        ? "admin/logout"
        : "logout";
    return this.httpClient.post(
      `${environment.SSO_URL}/sso/auth/${endpoint}`,
      body
    );
  }

  async ensureValidSession() {
    const token = this.store.selectSnapshot(SessionState.ssoToken);
    const expiresAt = this.store.selectSnapshot(SessionState.expiresAt);
    const expiresAtMs = expiresAt ? Date.parse(expiresAt) : Number.NaN;

    if (
      token &&
      Number.isFinite(expiresAtMs) &&
      expiresAtMs - Date.now() > REFRESH_LEEWAY_MS
    ) {
      try {
        await firstValueFrom(this.store.dispatch(new SetUser(token)));
        this.startAutoRefreshAt(expiresAtMs);
        return true;
      } catch {
        return this.refreshToken();
      }
    }

    return this.refreshToken();
  }

  refreshToken() {
    if (!this.refreshPromise) {
      const refreshPromise = this.performTokenRefresh();
      this.refreshPromise = refreshPromise;
      refreshPromise.then(
        () => this.clearRefreshPromise(refreshPromise),
        () => this.clearRefreshPromise(refreshPromise)
      );
    }

    return this.refreshPromise;
  }

  private async performTokenRefresh() {
    const sessionVersion = this.sessionVersion;
    const currentRefreshToken = this.store.selectSnapshot(
      SessionState.refreshToken
    );

    if (!currentRefreshToken) {
      console.warn("No refresh token available");
      this.endSession();
      return false;
    }

    try {
      const accessToken = this.store.selectSnapshot(SessionState.ssoToken);
      const endpoint =
        accessToken && new TokenUtils(accessToken).isPlatformAdministrator()
          ? "admin/refresh"
          : "refresh";
      const response = await firstValueFrom(
        this.httpClient.post<IAuthLoginResponse>(
          `${environment.SSO_URL}/sso/auth/${endpoint}`,
          {
            refreshToken: currentRefreshToken,
          }
        )
      );
      if (!this.isCurrentRefresh(sessionVersion, currentRefreshToken)) {
        return this.hasUsableAccessToken();
      }
      await firstValueFrom(
        this.store.dispatch([
          new SetSession(
            response.data.accessToken,
            response.data.refreshToken,
            response.data.expiresIn
          ),
          new SetUser(response.data.accessToken),
        ])
      );
      this.startAutoRefresh(response.data.expiresIn);
      return true;
    } catch (error) {
      if (!this.isCurrentRefresh(sessionVersion, currentRefreshToken)) {
        return this.hasUsableAccessToken();
      }
      console.error("Failed to refresh token:", error);
      this.endSession();
      return false;
    }
  }

  private clearRefreshPromise(refreshPromise: Promise<boolean>) {
    if (this.refreshPromise === refreshPromise) {
      this.refreshPromise = null;
    }
  }

  private isCurrentRefresh(sessionVersion: number, refreshToken: string) {
    return (
      this.sessionVersion === sessionVersion &&
      this.store.selectSnapshot(SessionState.refreshToken) === refreshToken
    );
  }

  private hasUsableAccessToken() {
    const token = this.store.selectSnapshot(SessionState.ssoToken);
    const expiresAt = this.store.selectSnapshot(SessionState.expiresAt);
    return Boolean(token && expiresAt && Date.parse(expiresAt) > Date.now());
  }

  private invalidatePendingRefresh() {
    this.sessionVersion += 1;
    this.refreshPromise = null;
  }

  private startAutoRefresh(expiresIn?: number) {
    const expiresAtMs =
      Date.now() + (expiresIn ?? DEFAULT_SESSION_SECONDS) * 1000;
    this.startAutoRefreshAt(expiresAtMs);
  }

  private startAutoRefreshAt(expiresAtMs: number) {
    this.stopAutoRefresh();
    const refreshMs = Math.max(expiresAtMs - Date.now() - REFRESH_LEEWAY_MS, 0);
    this.refreshTimeout = window.setTimeout(
      () => void this.handleAutoRefresh(),
      refreshMs
    );
  }

  private async handleAutoRefresh() {
    const token = this.store.selectSnapshot(SessionState.ssoToken);
    if (!token) {
      this.stopAutoRefresh();
      return;
    }

    const refreshed = await this.refreshToken();
    if (!refreshed) {
      await this.router.navigate(["/login"]);
    }
  }

  private stopAutoRefresh() {
    if (this.refreshTimeout !== null) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  startSession(response: IAuthLoginResponse) {
    if (response.data) {
      this.invalidatePendingRefresh();
      this.store.dispatch([
        new SetSession(
          response.data.accessToken,
          response.data.refreshToken,
          response.data.expiresIn
        ),
        new SetUser(response.data.accessToken),
      ]);
      this.startAutoRefresh(response.data.expiresIn);
    }
  }

  endSession() {
    this.invalidatePendingRefresh();
    this.stopAutoRefresh();
    this.store.dispatch([
      new ClearSession(),
      new SetCurrentTenant(null),
      new SetCurrentTenantBusinessName(null),
      new ClearAccessibleModules(),
      new ClearCompanyGeoreference(),
      new ClearFavorites(),
    ]);
  }

  requestReset(rut: string) {
    return this.httpClient.get(
      `${environment.SSO_URL}/sso/user/reset?rut=${rut}`
    );
  }

  validateResetCode(rut: string, code: string) {
    return this.httpClient.get<IValidateCodeResponse>(
      `${environment.SSO_URL}/sso/User/reset/validate-code?rut=${rut}&code=${code}`
    );
  }

  resetPassword(rut: string, code: string, password: string) {
    return this.httpClient.put<IResetPasswordResponse>(
      `${environment.SSO_URL}/sso/user/password`,
      {
        rut,
        code,
        password,
      }
    );
  }

  completeFirstLoginPassword(ticket: string, newPassword: string) {
    return this.httpClient.put<void>(
      `${environment.SSO_URL}/sso/user/password/first-login`,
      {
        ticket,
        newPassword,
      }
    );
  }

  ngOnDestroy() {
    this.stopAutoRefresh();
  }
}
